import type { SuperadminAuditAction, SuperadminAuditEntryView } from '@molt/shared';
import { Link } from '@tanstack/react-router';

import { RelativeTime } from '@/components/RelativeTime';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  ADMIN_TD_CLASS,
  ADMIN_TH_CLASS,
  ADMIN_THEAD_CLASS,
  ADMIN_TR_CLASS,
  AdminTableFrame,
} from './AdminConsole';

/**
 * SA-9 — the Audit tab over `superadmin_audit_entries` (SA-11), newest first.
 *
 * An entry names at most one of its four targets in practice; the first set one
 * is shown, an account target as a link to its console page. The actor is a
 * signed-in account or, for a CLI grant, the operator string — never both. Its
 * name is bounded and truncated, as the Users tab's account-holder cell is, so a
 * long display name never doubles a row's height.
 */

/** Key suffix = the dotted wire name; the pin iterates `SUPERADMIN_AUDIT_ACTIONS`. */
export function adminAuditActionKey(action: SuperadminAuditAction): string {
  return `admin.audit.action.${action}`;
}

export interface AdminAuditTableProps {
  readonly items: readonly SuperadminAuditEntryView[];
}

export function AdminAuditTable({ items }: AdminAuditTableProps) {
  const { t } = useInterfaceLanguage();
  return (
    <AdminTableFrame label={t('admin.audit.tableLabel')}>
      <thead className={ADMIN_THEAD_CLASS}>
        <tr>
          <th className={ADMIN_TH_CLASS}>{t('admin.audit.column.when')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.audit.column.action')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.audit.column.actor')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.audit.column.target')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.audit.column.reason')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((entry) => (
          <tr key={entry.id} className={ADMIN_TR_CLASS}>
            <td className={`${ADMIN_TD_CLASS} whitespace-nowrap text-ink-muted`}>
              <RelativeTime value={entry.createdAt} />
            </td>
            <td className={`${ADMIN_TD_CLASS} min-w-44 font-medium text-ink`}>
              {t(adminAuditActionKey(entry.action))}
            </td>
            <td className={ADMIN_TD_CLASS}>
              {entry.actor !== null ? (
                <Link
                  to="/admin/users/$userId"
                  params={{ userId: entry.actor.id }}
                  className="block max-w-40 truncate text-ink underline-offset-2 hover:underline"
                >
                  {entry.actor.displayName === ''
                    ? `@${entry.actor.username}`
                    : entry.actor.displayName}
                </Link>
              ) : entry.operator !== null ? (
                <span className="text-ink">
                  {t('admin.audit.operator', { operator: entry.operator })}
                </span>
              ) : (
                <span className="text-ink-muted">{t('admin.audit.noActor')}</span>
              )}
            </td>
            <td className={`${ADMIN_TD_CLASS} min-w-64 wrap-anywhere`}>
              <AuditTarget entry={entry} />
            </td>
            <td className={`${ADMIN_TD_CLASS} max-w-72 break-words`}>
              {entry.reason === null ? (
                <span className="text-ink-muted">{t('admin.audit.noReason')}</span>
              ) : (
                <span className="text-ink">{entry.reason}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </AdminTableFrame>
  );
}

function AuditTarget({ entry }: { readonly entry: SuperadminAuditEntryView }) {
  const { t } = useInterfaceLanguage();
  if (entry.targetUserId !== null) {
    return (
      <Link
        to="/admin/users/$userId"
        params={{ userId: entry.targetUserId }}
        className="text-ink underline-offset-2 hover:underline"
      >
        {t('admin.audit.target.user', { id: entry.targetUserId })}
      </Link>
    );
  }
  if (entry.targetBoardId !== null) {
    return (
      <span className="text-ink">
        {t('admin.audit.target.board', { id: entry.targetBoardId })}
      </span>
    );
  }
  if (entry.targetWorkspaceId !== null) {
    return (
      <span className="text-ink">
        {t('admin.audit.target.workspace', { id: entry.targetWorkspaceId })}
      </span>
    );
  }
  if (entry.targetEnterpriseId !== null) {
    return (
      <span className="text-ink">
        {t('admin.audit.target.enterprise', { id: entry.targetEnterpriseId })}
      </span>
    );
  }
  return <span className="text-ink-muted">{t('admin.audit.target.none')}</span>;
}
